import { AfterContentInit, Component, ComponentRef, ContentChildren, effect, inject, Injector, input, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Linkable } from '../../entities/entityLink/linkable';
import { NxTemplateDirective } from '../../directives/nx-template.directive';
import { GET_LINKED_ENTITIES_QUERY, GetLinkedEntitiesResponse, GetLinkedEntitiesVariables } from './graphql';
import { LinkedEntityConnection } from '../../entities/entityLink/linkedEntityConnection';
import { EntityRowRegistry } from '../../services/entity-link/entity-row-renderer.service';
import { ReactiveContainer } from '../../containers/reactive-container';
import { SIDEBAR_CONTEXT } from '../../services/sidebar/sidebar-context';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { Reloadable } from '../../services/command/command.service';

@Component({
  selector: 'entity-link-list',
  templateUrl: './entity-link-list.component.html'
})
export class EntityLinkListComponent implements OnInit, OnDestroy, AfterContentInit, Reloadable {

  @ViewChild('entityContainer', { read: ViewContainerRef, static: true }) entityContainer!: ViewContainerRef;

  // Dependencies
  private readonly entityRowRegistry = inject(EntityRowRegistry);
  private readonly injector = inject(Injector);
  private readonly refetchCoordinator = inject(RefetchCoordinator);

  // Sidebar context - true if this component is rendered inside a sidebar
  readonly isInSidebar = inject(SIDEBAR_CONTEXT, { optional: true }) ?? false;

  // Signal inputs
  readonly sourceEntity = input<Linkable>();
  readonly targetEntityType = input<string>();
  readonly linkType = input<string>();
  readonly headline = input<string>();
  readonly emptyMessage = input<string>();
  readonly createEntity = input<boolean>(false);
  readonly createEntityLabel = input<string>();

  /**
   * All templates that are inside the entity-link-list tag in the parent component
   */
  @ContentChildren(NxTemplateDirective) templates!: QueryList<NxTemplateDirective>;

  /**
   * Custom template for the create button area.
   * Allows parent components to provide their own button/action.
   */
  createButtonTemplate: TemplateRef<any> | undefined;

  // ReactiveContainer for GraphQL query using new getLinkedEntities
  private readonly container = new ReactiveContainer<GetLinkedEntitiesResponse>(
    GET_LINKED_ENTITIES_QUERY,
    { useGlobalLoading: false }
  );

  // Reactive query - automatically refetches when sourceEntity, targetEntityType, or linkType changes
  readonly data = this.container.fromSignals(
    [this.sourceEntity, this.targetEntityType, this.linkType],
    ([source, linkedEntityType, linkType]): GetLinkedEntitiesVariables => ({
      entityId: source?.id!,
      linkedEntityType: linkedEntityType,
      linkType: linkType,
      pageNumber: 0,
      pageSize: 100,
      sortProperties: ['id'],
      sortDirection: 'asc'
    }),
    { skipWhen: ([source]) => !source?.id }
  );

  // Derived signals from container
  readonly linkedEntities = this.container.selectWithDefault(
    d => d.getLinkedEntities.content,
    [] as LinkedEntityConnection[]
  );
  readonly loading = this.container.loading;
  readonly error = this.container.error;
  readonly totalElements = this.container.selectWithDefault(
    d => d.getLinkedEntities.totalElements,
    0
  );

  private renderedComponents: ComponentRef<any>[] = [];
  private unregisterRefetch?: () => void;

  constructor() {
    // Effect to render components when linkedEntities changes
    effect(() => {
      const connections = this.linkedEntities();
      this.renderEntityComponents(connections);
    });
  }

  ngOnInit(): void {
    // Register with RefetchCoordinator as detail view
    const source = this.sourceEntity();
    if (source?.id && source?.entityType) {
      this.unregisterRefetch = this.refetchCoordinator.register(
        this,
        source.entityType,
        source.id
      );
    }
  }

  /**
   * Walks through all templates and assigns them to the corresponding variables
   */
  ngAfterContentInit(): void {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'createButton':
          this.createButtonTemplate = item.template;
          break;
      }
    });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
    this.clearRenderedComponents();
  }

  reload(): void {
    this.container.reload();
  }

  private renderEntityComponents(connections: LinkedEntityConnection[]): void {
    this.clearRenderedComponents();

    if (!connections.length || !this.entityContainer) {
      return;
    }

    connections.forEach(connection => {
      const renderer = this.entityRowRegistry.getRenderer(connection.linkedEntity.entityType);
      if (renderer) {
        const componentRef = renderer.render(connection.linkedEntity, this.entityContainer, this.injector);
        this.renderedComponents.push(componentRef);
      } else {
        console.warn(`No renderer found for entity type: ${connection.linkedEntity.entityType}`);
      }
    });
  }

  private clearRenderedComponents(): void {
    this.renderedComponents.forEach(componentRef => componentRef.destroy());
    this.renderedComponents = [];
    if (this.entityContainer) {
      this.entityContainer.clear();
    }
  }

}
